import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";

import {
  getAllImagesFn,
  PatchAllImagesFn,
  postAllImagesFn,
} from "../../Services/AllImages";

import { submitSelectedImageFn } from "../../Services/Images";

const ITEMS_PER_PAGE = 56;

const AllImagesComponent = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [image, setImage] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["get-all-images"],
    queryFn: getAllImagesFn,
  });

  const allImages = data?.data?.file || [];

  const filteredImages = allImages.filter((img) =>
    img?.key?.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.ceil(filteredImages.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const visibleImages = filteredImages.slice(startIndex, endIndex);

  const handleImageClick = (img) => {
    setSelectedImage(img);
    setOpenModal(true);
  };

  const { mutate: submitImage, isLoading: submitting } = useMutation(
    PatchAllImagesFn,
    {
      onSuccess: (res) => {
        if (res?.data?.response_code === 200) {
          toast.success(res?.data?.message);
          setOpenModal(false);
          setSelectedImage(null);
          setImage(null);
          refetch();
        } else {
          toast.warn(res?.data?.message);
        }
      },
      onError: () => {
        toast.error("Failed to submit image");
      },
    },
  );

  const handleSubmit = () => {
    if (!selectedImage) {
      toast.error("Please select an image");
      return;
    }

    const formData = new FormData();
    formData.append("key", selectedImage.key);
    formData.append("file", image);

    submitImage(formData);
  };

  return (
    <div className="w-full mx-auto px-4 py-8">
      <div className="mb-4">
        <input
          value={search}
          placeholder="Search by key..."
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 px-4 py-2 rounded w-72
                     focus:outline-none focus:border-green-500"
        />
      </div>

      {isLoading && (
        <p className="text-center text-gray-500 mt-10">Loading images...</p>
      )}

      <div className="grid md:grid-cols-7 grid-cols-2 gap-2">
        {visibleImages.map((img, index) => (
          <div
            key={index}
            className="overflow-hidden rounded-lg shadow hover:shadow-lg transition relative cursor-pointer"
            onClick={() => handleImageClick(img)}
          >
            <img
              src={`${img.url}?v=${img.updated_at || Date.now()}`}
              alt={`Image ${index + 1}`}
              className="w-full h-36 rounded-lg object-fill"
              loading="lazy"
            />

            <div className="absolute bottom-0 left-0 w-full bg-gradient-to-r from-yellow-300 to-[#e63354] text-white text-[9px] font-semibold p-2 text-center">
              {img?.key || "--"}
            </div>
          </div>
        ))}
      </div>

      {!isLoading && filteredImages.length === 0 && (
        <p className="text-center text-gray-500 mt-10">No images found</p>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2 mt-6">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-3 py-1 border rounded text-sm disabled:text-gray-400"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 rounded text-sm border
                  ${
                    currentPage === page
                      ? "bg-green-600 text-white border-green-600"
                      : "border-gray-400 hover:bg-gray-100"
                  }`}
              >
                {page}
              </button>
            );
          })}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-3 py-1 border rounded text-sm disabled:text-gray-400"
          >
            Next
          </button>
        </div>
      )}

      {openModal && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-lg w-[90%] max-w-4xl relative">
            {/* CLOSE */}
            <button
              onClick={() => setOpenModal(false)}
              className="absolute top-2 right-3 text-xl text-gray-600 hover:text-black"
            >
              ✕
            </button>

            {/* IMAGE */}
            <div className="p-4">
              <img
                src={`${selectedImage.url}?v=${selectedImage.updated_at || Date.now()}`}
                alt="Preview"
                className="w-full max-h-[70vh] object-contain rounded"
              />
            </div>

            <div className=" py-4 flex items-center justify-center">
              <input
                type="file"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </div>

            <div className="border-t p-4 flex justify-between items-center">
              <p className="text-sm text-gray-600">{selectedImage?.key}</p>

              <div className="flex gap-2">
                <button
                  onClick={() => setOpenModal(false)}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancel
                </button>

                <button
                  disabled={submitting}
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Select & Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllImagesComponent;
